import React from "react";
import { Navbar2 } from "../../Components/Navbar2/Navbar2";
import CheckoutItem from "../../Components/CheckoutItem";
import { useNavigate } from "react-router-dom";
import CheckoutForm from "../../Components/CheckoutForm";
import useCheckout from "./useCheckout";
import PaymentModal from "../../Components/PaymentModal";
import FooterNew from "../../Components/LandingNew/FooterNew";

export default function Checkout() {
  const {
    itemDetail,
    subTotal,
    discountPrice,
    serviceCharges,
    total,
    modalType,
    userData,
    handelModalType,
    isSubscription,
    access,
    hasValidCheckoutItem,
  } = useCheckout();
  const navigate = useNavigate();

  if (!hasValidCheckoutItem && !modalType) {
    return (
      <div className="bab-new-bg min-h-screen text-white flex flex-col justify-between">
        <Navbar2 />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-3xl mb-4">
            🛒
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Item Selected</h3>
          <p className="text-zinc-400 text-sm max-w-sm mb-6">
            Your checkout session is currently empty. Please select a product, audiobook, or subscription to proceed.
          </p>
          <button
            onClick={() => navigate("/ProductCollection")}
            className="px-6 py-2.5 rounded-xl bg-secondary text-white font-bold text-sm shadow-lg shadow-secondary/30 hover:bg-secondary/90 transition-all active:scale-95"
          >
            Browse Collection
          </button>
        </div>
        <FooterNew />
      </div>
    );
  }

  return (
    <div className="bab-new-bg min-h-screen text-white flex flex-col justify-between">
      {modalType && (
        <PaymentModal
          handelModalType={handelModalType}
          type={modalType}
          isSubscription={isSubscription}
        />
      )}

      <div>
        <Navbar2 />
        <div className="min-w-screen bab-new-bg px-3 sm:px-6 lg:px-20 xl:px-32 bg-black py-6 md:py-12">
          <div className="w-full shadow-2xl bg-[#141414] border border-gray-900 rounded-2xl px-4 py-6 sm:px-6 sm:py-8 md:p-12 text-white">
            <div className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Left Column: Checkout Items & Summary */}
                <div className="lg:col-span-7 xl:col-span-8 space-y-6">
                  <h4 className="text-white font-bold text-xl md:text-2xl tracking-tight mb-4 flex items-center gap-2">
                    {isSubscription ? (
                      <>
                        <span className="text-secondary">👑</span>
                        <span>Confirm Subscription</span>
                      </>
                    ) : (
                      <>
                        <span>Checkout Item</span>
                        {access?.isPpv ? (
                          <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            Pay-Per-View
                          </span>
                        ) : (
                          <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-secondary/15 text-secondary border border-secondary/30">
                            One-Time Buy
                          </span>
                        )}
                      </>
                    )}
                  </h4>
                  <div className="w-full mx-auto text-white font-light border-b border-gray-800 pb-6 space-y-6">
                    <CheckoutItem {...itemDetail} isSubscription={isSubscription} />
                  </div>

                  {/* Discount Code */}
                  <div className="py-4 border-b border-gray-800">
                    <div className="flex flex-col sm:flex-row gap-3 items-end max-w-md">
                      <div className="w-full">
                        <label className="block text-gray-300 font-semibold text-sm mb-2 ml-1">
                          Discount code
                        </label>
                        <input
                          className="w-full px-4 py-3 bg-[#1c1c1c] border border-gray-850 text-white placeholder-gray-500 rounded-xl focus:outline-none focus:border-secondary transition-all outline-none"
                          placeholder="XXXXXX"
                          type="text"
                        />
                      </div>
                      <button className="w-full sm:w-auto bg-secondary hover:bg-orange-500 text-white rounded-xl px-6 py-3 font-semibold transition-all hover:scale-[1.01] active:scale-95 shadow-md shadow-secondary/15 whitespace-nowrap">
                        APPLY
                      </button>
                    </div>
                  </div>

                  {/* Pricing Details */}
                  <div className="py-2 border-b border-gray-800 space-y-3">
                    <div className="w-full flex items-center justify-between text-sm md:text-base">
                      <span className="text-gray-400">Subtotal</span>
                      <span className="font-semibold text-white">${subTotal}</span>
                    </div>
                    <div className="w-full flex items-center justify-between text-sm md:text-base">
                      <span className="text-gray-400">Discount</span>
                      <span className="font-semibold text-green-500">${discountPrice}</span>
                    </div>
                    {!isSubscription && (
                      <div className="w-full flex items-center justify-between text-sm md:text-base">
                        <span className="text-gray-400">Service Charges</span>
                        <span className="font-semibold text-white">${serviceCharges}</span>
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="pt-2 text-xl">
                    <div className="w-full flex items-center justify-between">
                      <span className="text-gray-300 font-medium">Total</span>
                      <span className="font-bold text-2xl text-secondary">${total}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Billing Information & Payment Card Form */}
                <div className="lg:col-span-5 xl:col-span-4 space-y-6">
                  {/* Billing Card */}
                  <div className="w-full mx-auto rounded-2xl bg-[#1c1c1c] border border-gray-800 p-4 sm:p-5 text-white shadow-lg">
                    <h5 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                      Billing Information
                    </h5>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-400 text-xs md:text-sm">Customer Name</span>
                        <span className="font-semibold text-white text-xs md:text-sm truncate max-w-[200px]" title={userData?.name}>
                          {userData?.name || "N/A"}
                        </span>
                      </div>
                      {userData?.email && (
                        <div className="flex justify-between items-center py-1 pt-3 border-t border-gray-800/60">
                          <span className="text-gray-400 text-xs md:text-sm">Email Address</span>
                          <span className="font-semibold text-white text-xs md:text-sm truncate max-w-[200px]" title={userData?.email}>
                            {userData?.email}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Card Form wrapper */}
                  <div className="w-full mx-auto rounded-2xl bg-[#1c1c1c] border border-gray-800 p-4 sm:p-5 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-850">
                      <span className="font-bold text-sm text-gray-300">Payment Details</span>
                      <img
                        src="https://leadershipmemphis.org/wp-content/uploads/2020/08/780370.png"
                        className="h-6 opacity-90 rounded bg-white px-2 py-0.5"
                        alt="Stripe"
                      />
                    </div>
                    <div>
                      <CheckoutForm
                        handelModalType={handelModalType}
                        amount={total}
                        id={itemDetail?.id}
                        isSubscription={isSubscription}
                        packageId={isSubscription ? itemDetail?.id : undefined}
                        packageData={itemDetail}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterNew />
    </div>
  );
}
